import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    CircularProgress,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    LinearProgress,
    Stepper,
    Step,
    StepLabel,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    QrCodeScanner as QrScannerIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import apiService from '../../services/api';

const DeliveryConfirmation = () => {
    // New state for scanned run sheet workflow
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [processingBarcodes, setProcessingBarcodes] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [detectedDeliveries, setDetectedDeliveries] = useState([]);
    const [selectedDeliveries, setSelectedDeliveries] = useState(new Set());
    const [deliveryStatuses, setDeliveryStatuses] = useState(new Map());
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [imageViewDialog, setImageViewDialog] = useState(false);
    
    // State for delivery date and receiver name fields
    const [deliveryDates, setDeliveryDates] = useState(new Map());
    const [receiverNames, setReceiverNames] = useState(new Map());
    
    // State for file type tracking
    const [uploadedFileType, setUploadedFileType] = useState(null);
    
    const fileInputRef = useRef(null);
    const resetTimeoutRef = useRef(null);
    
    const steps = ['Upload Signed Run Sheet', 'Process & Detect Deliveries', 'Confirm Deliveries'];

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (resetTimeoutRef.current) {
                clearTimeout(resetTimeoutRef.current);
            }
        };
    }, []);

    // Recent deliveries functionality removed as requested

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Debug file information
        console.log('File upload debug:', {
            fileName: file.name,
            fileSize: file.size,
            fileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
            fileType: file.type,
            lastModified: new Date(file.lastModified).toISOString()
        });

        // Enhanced file type validation
        const isImage = file.type.startsWith('image/');
        const isPdf = file.type === 'application/pdf';
        
        console.log('File type detection:', { isImage, isPdf, fileType: file.type });
        
        if (!isImage && !isPdf) {
            setError('Please upload a valid image file (JPG, PNG) or PDF document');
            return;
        }

        // Validate file size (max 20MB for scanned documents)
        const maxSize = 20 * 1024 * 1024; // 20MB
        console.log('File size validation:', { fileSize: file.size, maxSize, isOverLimit: file.size > maxSize });
        
        if (file.size > maxSize) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            setError(`File size (${fileSizeMB}MB) exceeds the maximum allowed size of 20MB. Please compress the file or use a smaller document.`);
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const base64String = e.target.result.split(',')[1]; // Remove data:...;base64, prefix
            
            // Set preview based on file type
            if (isPdf) {
                // For PDFs, show a PDF icon or placeholder
                setImagePreview('data:image/svg+xml;base64,' + btoa(`
                    <svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
                        <rect width="200" height="200" fill="#f5f5f5"/>
                        <text x="100" y="90" text-anchor="middle" font-family="Arial" font-size="16" fill="#666">PDF Document</text>
                        <text x="100" y="110" text-anchor="middle" font-family="Arial" font-size="12" fill="#999">${file.name}</text>
                        <text x="100" y="130" text-anchor="middle" font-family="Arial" font-size="10" fill="#999">Click to process</text>
                    </svg>
                `));
            } else {
                setImagePreview(e.target.result);
            }
            
            setUploadedImage(base64String);
            setUploadedFileType(isPdf ? 'pdf' : 'image');
            setError('');
            setActiveStep(1); // Move to processing step
            
            // Automatically process the file after upload
            await processImageWithData(base64String, isPdf ? 'pdf' : 'image');
        };
        reader.readAsDataURL(file);
    };

    const checkDeliveryStatuses = async (awbNumbers) => {
        try {
            const response = await apiService.post('/delivery-run-sheet/check-delivery-status', {
                awb_numbers: awbNumbers
            });

            if (response.data.success) {
                const statusMap = new Map();
                response.data.data.delivery_statuses.forEach(status => {
                    statusMap.set(status.awb_no, status);
                });
                setDeliveryStatuses(statusMap);
                return statusMap;
            } else {
                console.error('Failed to check delivery status:', response.data.message);
                return new Map();
            }
        } catch (error) {
            console.error('Error checking delivery status:', error);
            return new Map();
        }
    };

    const processImageWithData = async (fileData, fileType = 'image') => {
        console.log(`Starting ${fileType} processing...`);
        setProcessingBarcodes(true);
        setError('');

        try {
            console.log(`Sending API request to process ${fileType}`);
            console.log('File upload debug info:', {
                fileType: fileType,
                base64Length: fileData.length,
                estimatedSizeMB: (fileData.length * 0.75 / (1024 * 1024)).toFixed(2)
            });
            
            const response = await apiService.post('/delivery-run-sheet/process-scanned-image', {
                image_data: fileData,
                image_type: 'delivery_run_sheet',
                file_type: fileType
            });

            console.log('API response received:', response);

            if (response.data.success) {
                const detectedDeliveries = response.data.data.detected_deliveries || [];
                setDetectedDeliveries(detectedDeliveries);
                console.log('Detected deliveries:', detectedDeliveries);
                
                if (detectedDeliveries.length === 0) {
                    setError('No barcodes detected in the document. Please ensure the document is clear and contains barcodes.');
                } else {
                    // Check delivery status for all detected AWBs
                    const awbNumbers = detectedDeliveries.map(d => d.awb_no);
                    await checkDeliveryStatuses(awbNumbers);
                    
                    setActiveStep(2); // Move to confirmation step

                    // No auto-selection - user will manually select deliveries
                    setSelectedDeliveries(new Set());

                    // Set default delivery dates for all detected deliveries
                    const today = new Date().toISOString().split('T')[0];
                    const defaultDates = new Map();
                    detectedDeliveries.forEach(delivery => {
                        defaultDates.set(delivery.awb_no, today);
                    });
                    setDeliveryDates(defaultDates);

                    console.log('Processing successful, moving to step 2');
                    console.log('Detected deliveries:', detectedDeliveries.length);
                }
            } else {
                console.error('API response error:', response);
                setError(response.message || 'Failed to process image');
            }
        } catch (error) {
            console.error('Document processing error:', error);
            
            // Handle specific HTTP errors
            if (error.response) {
                if (error.response.status === 413) {
                    setError('File size too large. Please compress the document or use a smaller file (max 20MB).');
                } else {
                    setError('Failed to process document: ' + (error.response.data?.message || error.message));
                }
            } else {
                setError('Failed to process document: ' + error.message);
            }
        } finally {
            setProcessingBarcodes(false);
            console.log('Document processing completed');
        }
    };

    const processImage = async () => {
        if (!uploadedImage) {
            setError('Please upload a file first');
            return;
        }

        // Use tracked file type
        const fileType = uploadedFileType || 'image';
        await processImageWithData(uploadedImage, fileType);
    };

    const toggleDeliverySelection = (awbNo) => {
        const newSelected = new Set(selectedDeliveries);
        if (newSelected.has(awbNo)) {
            newSelected.delete(awbNo);
        } else {
            newSelected.add(awbNo);
        }
        setSelectedDeliveries(newSelected);
    };

    const selectAllDeliveries = () => {
        const allAwbs = new Set(
            detectedDeliveries
                .filter(d => deliveryStatuses.get(d.awb_no)?.can_deliver !== false)
                .map(d => d.awb_no)
        );
        setSelectedDeliveries(allAwbs);
    };

    const clearAllSelections = () => {
        setSelectedDeliveries(new Set());
    };

    const clearAll = () => {
        // Clear any pending auto-reset timeout
        if (resetTimeoutRef.current) {
            clearTimeout(resetTimeoutRef.current);
            resetTimeoutRef.current = null;
        }
        
        setUploadedImage(null);
        setImagePreview('');
        setDetectedDeliveries([]);
        setSelectedDeliveries(new Set());
        setDeliveryStatuses(new Map());
        setDeliveryDates(new Map());
        setReceiverNames(new Map());
        setUploadedFileType(null);
        setActiveStep(0);
        setError('');
        setSuccess('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleBatchConfirmDelivery = () => {
        if (selectedDeliveries.size === 0) {
            setError('Please select at least one delivery to confirm');
            return;
        }
        setConfirmDialogOpen(true);
    };

    const confirmBatchDelivery = async () => {
        if (selectedDeliveries.size === 0) {
            setError('No deliveries selected');
            return;
        }

        // Validate that all selected deliveries have delivery dates
        const selectedAwbs = Array.from(selectedDeliveries);
        const missingDates = selectedAwbs.filter(awbNo => !deliveryDates.get(awbNo));

        if (missingDates.length > 0) {
            setError(`Please provide delivery dates for AWBs: ${missingDates.join(', ')}`);
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');
        setConfirmDialogOpen(false);

        try {
            // Prepare delivery data with individual delivery dates and receiver names
            const deliveryData = selectedAwbs.map(awbNo => ({
                awb_no: awbNo,
                delivery_date: deliveryDates.get(awbNo) || new Date().toISOString().split('T')[0],
                receiver_name: receiverNames.get(awbNo) || ''
            }));

            // Identify unchecked/undelivered AWBs that should be removed from DRS
            const allDetectedAwbs = detectedDeliveries.map(d => d.awb_no);
            const uncheckedAwbs = allDetectedAwbs.filter(awbNo => !selectedDeliveries.has(awbNo));

            console.log('Delivery confirmation summary:', {
                totalDetected: allDetectedAwbs.length,
                selectedForDelivery: selectedAwbs.length,
                uncheckedToRemove: uncheckedAwbs.length,
                uncheckedAwbs: uncheckedAwbs
            });

            // Use tracked file type
            const fileType = uploadedFileType || 'image';

            const response = await apiService.post('/delivery-run-sheet/batch-confirm-delivery', {
                deliveries: deliveryData,
                scanned_image: uploadedImage,
                file_type: fileType,
                remove_undelivered_from_drs: uncheckedAwbs.length > 0,
                undelivered_awbs: uncheckedAwbs
            });

            if (response.data && response.data.success) {
                const confirmed = response.data.data?.confirmed_deliveries?.length || 0;
                const skipped = response.data.data?.skipped_deliveries?.length || 0;
                const errors = response.data.data?.errors?.length || 0;
                const removedFromDrs = response.data.data?.removed_from_drs?.length || 0;

                let successMessage = `Successfully confirmed delivery for ${confirmed} shipments`;
                if (skipped > 0) {
                    successMessage += `, ${skipped} already delivered`;
                }
                if (errors > 0) {
                    successMessage += `, ${errors} failed`;
                }
                if (removedFromDrs > 0) {
                    successMessage += `, ${removedFromDrs} undelivered AWBs removed from DRS`;
                }
                successMessage += '. Form will reset automatically in 3 seconds...';

                setSuccess(successMessage);

                // Show detailed information about operations
                if (skipped > 0) {
                    console.log('Skipped deliveries:', response.data.data.skipped_deliveries);
                }
                if (removedFromDrs > 0) {
                    console.log('Removed from DRS:', response.data.data.removed_from_drs);
                }

                // Clear any existing timeout
                if (resetTimeoutRef.current) {
                    clearTimeout(resetTimeoutRef.current);
                }

                // Reset form to step 1 after showing success message for 3 seconds
                resetTimeoutRef.current = setTimeout(() => {
                    clearAll();
                }, 3000);
            } else {
                setError(response.data?.message || response.message || 'Failed to confirm deliveries');
            }
        } catch (error) {
            console.error('Batch confirm delivery error:', error);

            // Handle specific HTTP errors
            if (error.response) {
                if (error.response.status === 413) {
                    setError('File size too large for confirmation. Please compress the document or use a smaller file (max 20MB).');
                } else {
                    setError('Failed to confirm deliveries: ' + (error.response.data?.message || error.message));
                }
            } else {
                setError('Failed to confirm deliveries: ' + error.message);
            }
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Upload Delivery Run Sheet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Scan and upload the delivery run sheet (image or PDF) to detect AWB numbers
                        </Typography>

                        {!imagePreview ? (
                            <Box sx={{ 
                                border: '2px dashed #ccc',
                                borderRadius: 2,
                                p: 6,
                                textAlign: 'center',
                                backgroundColor: '#f9f9f9'
                            }}>
                                <Stack spacing={3} alignItems="center">
                                    <UploadIcon sx={{ fontSize: 64, color: '#ccc' }} />
                                    <Typography variant="h6" color="text.secondary">
                                        Upload Scanned Delivery Run Sheet
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Supported formats: JPG, PNG, PDF (scanned documents)
                                        <br />
                                        Maximum file size: 20MB
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={<UploadIcon />}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        Choose File
                                    </Button>
                                </Stack>
                            </Box>
                        ) : (
                            <Box sx={{ 
                                border: '1px solid #ddd',
                                borderRadius: 2,
                                p: 2,
                                textAlign: 'center'
                            }}>
                                <img
                                    src={imagePreview}
                                    alt="Uploaded delivery run sheet"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '400px',
                                        borderRadius: '4px'
                                    }}
                                />
                                <Box sx={{ mt: 2 }}>
                                    <Stack direction="row" spacing={2} justifyContent="center">
                                        <Tooltip title="View full image">
                                            <IconButton onClick={() => setImageViewDialog(true)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            startIcon={<DeleteIcon />}
                                            onClick={clearAll}
                                        >
                                            Remove & Upload New
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<QrScannerIcon />}
                                            onClick={processImage}
                                            disabled={processingBarcodes}
                                        >
                                            {processingBarcodes ? 'Processing...' : 'Process Document'}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Box>
                        )}

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*,.pdf"
                            onChange={handleImageUpload}
                            style={{ display: 'none' }}
                        />
                    </Paper>
                );

            case 1:
                return (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Processing Scanned Document
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Detecting barcodes and extracting delivery information...
                        </Typography>

                        {processingBarcodes && (
                            <Box sx={{ mb: 3 }}>
                                <LinearProgress />
                                <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                                    Scanning for barcodes in the uploaded document...
                                </Typography>
                            </Box>
                        )}

                        <Stack direction="row" spacing={2} justifyContent="center">
                            <Button
                                variant="outlined"
                                onClick={() => setActiveStep(0)}
                                disabled={processingBarcodes}
                            >
                                Back to Upload
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<RefreshIcon />}
                                onClick={processImage}
                                disabled={processingBarcodes}
                            >
                                {processingBarcodes ? 'Processing...' : 'Reprocess Document'}
                            </Button>
                        </Stack>
                    </Paper>
                );

            case 2:
                return (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Detected Deliveries ({detectedDeliveries.length})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Review and confirm the deliveries detected from the scanned run sheet
                        </Typography>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            Select the deliveries to confirm. Enter delivery date and receiver name for each AWB.
                        </Alert>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <strong>New Fields:</strong> Please provide delivery date and receiver name for each AWB. Delivery date is required for confirmation.
                        </Alert>
                        
                        {detectedDeliveries.some(d => deliveryStatuses.get(d.awb_no)?.status === 'already_delivered') && (
                            <Alert severity="warning" sx={{ mb: 3 }}>
                                {detectedDeliveries.filter(d => deliveryStatuses.get(d.awb_no)?.status === 'already_delivered').length} AWB(s) 
                                have already been delivered and cannot be selected for confirmation.
                            </Alert>
                        )}

                        {detectedDeliveries.length > 0 && (
                            <>
                                <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={selectAllDeliveries}
                                    >
                                        Select All ({detectedDeliveries.filter(d => deliveryStatuses.get(d.awb_no)?.can_deliver !== false).length})
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={clearAllSelections}
                                    >
                                        Clear All
                                    </Button>
                                    <Chip
                                        label={`${selectedDeliveries.size} selected`}
                                        color="primary"
                                        variant="outlined"
                                    />
                                </Stack>

                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedDeliveries.size === detectedDeliveries.length && detectedDeliveries.length > 0}
                                                        indeterminate={selectedDeliveries.size > 0 && selectedDeliveries.size < detectedDeliveries.length}
                                                        onChange={() => selectedDeliveries.size === detectedDeliveries.length ? clearAllSelections() : selectAllDeliveries()}
                                                    />
                                                </TableCell>
                                                <TableCell>AWB Number</TableCell>
                                                <TableCell>From</TableCell>
                                                <TableCell>To</TableCell>
                                                <TableCell>Delivery Date</TableCell>
                                                <TableCell>Receiver Name</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {detectedDeliveries.map((delivery) => (
                                                <TableRow key={delivery.awb_no}>
                                                    <TableCell padding="checkbox">
                                                        <Checkbox
                                                            checked={selectedDeliveries.has(delivery.awb_no)}
                                                            disabled={deliveryStatuses.get(delivery.awb_no)?.can_deliver === false}
                                                            onChange={() => toggleDeliverySelection(delivery.awb_no)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {delivery.awb_no}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {delivery.from_customer}
                                                            <br />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {delivery.from_city}
                                                            </Typography>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography variant="body2">
                                                            {delivery.to_customer}
                                                            <br />
                                                            <Typography variant="caption" color="text.secondary">
                                                                {delivery.to_city}
                                                            </Typography>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <input
                                                            type="date"
                                                            value={deliveryDates.get(delivery.awb_no) || ''}
                                                            onChange={(e) => {
                                                                const newDates = new Map(deliveryDates);
                                                                newDates.set(delivery.awb_no, e.target.value);
                                                                setDeliveryDates(newDates);
                                                            }}
                                                            style={{
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                padding: '6px 8px',
                                                                fontSize: '12px',
                                                                width: '100%',
                                                                backgroundColor: deliveryStatuses.get(delivery.awb_no)?.can_deliver === false ? '#f5f5f5' : 'white'
                                                            }}
                                                            disabled={deliveryStatuses.get(delivery.awb_no)?.can_deliver === false}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <input
                                                            type="text"
                                                            placeholder="Receiver name"
                                                            value={receiverNames.get(delivery.awb_no) || ''}
                                                            onChange={(e) => {
                                                                const newNames = new Map(receiverNames);
                                                                newNames.set(delivery.awb_no, e.target.value);
                                                                setReceiverNames(newNames);
                                                            }}
                                                            style={{
                                                                border: '1px solid #ddd',
                                                                borderRadius: '4px',
                                                                padding: '6px 8px',
                                                                fontSize: '12px',
                                                                width: '100%',
                                                                backgroundColor: deliveryStatuses.get(delivery.awb_no)?.can_deliver === false ? '#f5f5f5' : 'white'
                                                            }}
                                                            disabled={deliveryStatuses.get(delivery.awb_no)?.can_deliver === false}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        {(() => {
                                                            const status = deliveryStatuses.get(delivery.awb_no);
                                                            if (status?.status === 'already_delivered') {
                                                                return (
                                                                    <>
                                                                        <Chip
                                                                            icon={<CheckCircleIcon />}
                                                                            label="Already Delivered"
                                                                            color="error"
                                                                            size="small"
                                                                        />
                                                                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                            Delivered on: {new Date(status.delivered_at).toLocaleDateString()}
                                                                        </Typography>
                                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                                            By: {status.delivered_by}
                                                                        </Typography>
                                                                    </>
                                                                );
                                                            } else if (status?.status === 'not_found') {
                                                                return (
                                                                    <Chip
                                                                        icon={<CancelIcon />}
                                                                        label="Not Found"
                                                                        color="warning"
                                                                        size="small"
                                                                    />
                                                                );
                                                            } else {
                                                                return (
                                                                    <Chip
                                                                        icon={<CheckCircleIcon />}
                                                                        label="Ready for Delivery"
                                                                        color="success"
                                                                        size="small"
                                                                    />
                                                                );
                                                            }
                                                        })()}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 3 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setActiveStep(1)}
                                    >
                                        Back to Processing
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                                        onClick={handleBatchConfirmDelivery}
                                        disabled={loading || selectedDeliveries.size === 0}
                                    >
                                        Confirm {selectedDeliveries.size} Deliveries
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </Paper>
                );

            default:
                return null;
        }
    };

    return (
        <DashboardLayout>
            <Box sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Delivery Confirmation
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Process signed delivery run sheets and confirm bulk deliveries
                    </Typography>
                </Box>

                {/* Progress Stepper */}
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Paper>

                <Grid container spacing={3}>
                    {/* Main Content Area */}
                    <Grid item xs={12}>
                        {/* Messages */}
                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}
                        
                        {success && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                {success}
                            </Alert>
                        )}

                        {/* Step Content */}
                        {renderStepContent()}

                        {/* Clear All Button */}
                        {(uploadedImage || detectedDeliveries.length > 0) && (
                            <Box sx={{ mt: 3, textAlign: 'center' }}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={clearAll}
                                >
                                    Clear All & Start Over
                                </Button>
                            </Box>
                        )}
                    </Grid>
                </Grid>

                {/* Batch Confirmation Dialog */}
                <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>Confirm Batch Delivery</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" gutterBottom>
                            Are you sure you want to confirm delivery for <strong>{selectedDeliveries.size}</strong> shipments?
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            This action cannot be undone and will mark all selected shipments as delivered.
                        </Typography>

                        {(() => {
                            const allDetectedAwbs = detectedDeliveries.map(d => d.awb_no);
                            const uncheckedAwbs = allDetectedAwbs.filter(awbNo => !selectedDeliveries.has(awbNo));
                            const totalDeliveries = detectedDeliveries.length;

                            return (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        <strong>Summary:</strong> Out of {totalDeliveries} detected shipments,
                                        {selectedDeliveries.size} will be marked as delivered and
                                        {uncheckedAwbs.length > 0 ? ` ${uncheckedAwbs.length} undelivered AWBs will be removed from the DRS table.` : ' no undelivered AWBs to remove.'}
                                    </Alert>

                                    {selectedDeliveries.size > 0 && (
                                        <Box sx={{
                                            backgroundColor: '#e8f5e8',
                                            p: 2,
                                            borderRadius: 1,
                                            mb: 2,
                                            maxHeight: '150px',
                                            overflowY: 'auto'
                                        }}>
                                            <Typography variant="body2" fontWeight="bold" gutterBottom color="success.main">
                                                ✓ Selected for Delivery ({selectedDeliveries.size}):
                                            </Typography>
                                            {Array.from(selectedDeliveries).map(awb => (
                                                <Chip
                                                    key={awb}
                                                    label={awb}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{ m: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    )}

                                    {uncheckedAwbs.length > 0 && (
                                        <Box sx={{
                                            backgroundColor: '#fff3e0',
                                            p: 2,
                                            borderRadius: 1,
                                            mb: 2,
                                            maxHeight: '150px',
                                            overflowY: 'auto'
                                        }}>
                                            <Typography variant="body2" fontWeight="bold" gutterBottom color="warning.main">
                                                🗑️ Will be removed from DRS ({uncheckedAwbs.length}):
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                                These undelivered AWBs will be removed from the current DRS to prevent duplicate associations and allow reprocessing.
                                            </Typography>
                                            {uncheckedAwbs.map(awb => (
                                                <Chip
                                                    key={awb}
                                                    label={awb}
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ m: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </>
                            );
                        })()}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
                        <Button
                            variant="contained"
                            onClick={confirmBatchDelivery}
                            autoFocus
                            disabled={loading}
                        >
                            {loading ? 'Processing...' : `Confirm ${selectedDeliveries.size} Deliveries`}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Image View Dialog */}
                <Dialog open={imageViewDialog} onClose={() => setImageViewDialog(false)} maxWidth="lg" fullWidth>
                    <DialogTitle>Scanned Delivery Run Sheet</DialogTitle>
                    <DialogContent>
                        {imagePreview && (
                            <Box sx={{ textAlign: 'center' }}>
                                <img
                                    src={imagePreview}
                                    alt="Full size delivery run sheet"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setImageViewDialog(false)}>Close</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </DashboardLayout>
    );
};

export default DeliveryConfirmation; 